/**
 * OrderForm - Lista de pedidos con estado de preparación
 *
 * Muestra todos los pedidos de la preparación con su estado actual.
 * Incluye:
 * - Lista de pedidos con información de tienda y cliente
 * - Búsqueda por nombre de cliente o referencia de pedido
 * - Estado de preparación (completado/en preparación)
 * - Información de courier y tracking
 * - Modal de detalles del pedido
 * - Integración con localStorage para fechas de completado
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@shared/components/ui/input';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Shop } from '@shared/components/ui/shop';
import { Avatar, AvatarImage, AvatarFallback } from '@shared/components/ui/shadcn/avatar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faSearch, faShoppingBag, faPrint, faCalendar } from '@fortawesome/pro-light-svg-icons';
import { OrderDetailsModal } from '../modals';
import { usePackingContext, usePrintOrder } from '../../hooks';
import { type OrderDetails } from '../../models';

export const OrderForm = () => {
  const { t } = useTranslation('packingModule');
  const { orders, ordersLoading, ordersError, getOrders, data } = usePackingContext() as any;
  const { printOrder, isOrderLoading } = usePrintOrder();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<OrderDetails | undefined>(undefined);
  const orderDetailsModalRef = useRef<any>(null);

  // Cargar pedidos solo la primera vez (si no existen en memoria)
  useEffect(() => {
    if (!orders || orders.length === 0) {
      getOrders();
    }
  }, [orders, getOrders]);

  // Función para contar productos totales incluyendo packs
  const getTotalProducts = (order: any): number => {
    if (!order.lines) return 0;

    return order.lines.reduce((total: number, line: any) => {
      // Si la línea tiene un pack, contar los productos dentro del pack
      if (line.pack && line.pack.lines) {
        return (
          total +
          line.pack.lines.reduce((packTotal: number, packLine: any) => {
            return packTotal + (packLine.qty || 1);
          }, 0)
        );
      }
      // Si es un producto individual, contar su cantidad
      return total + (line.qty || 1);
    }, 0);
  };

  // Filtrar pedidos basado en el término de búsqueda
  const filteredOrders =
    orders?.filter(
      (order: any) =>
        (order.customer?.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (order.ref?.toLowerCase() || '').includes(searchTerm.toLowerCase()),
    ) || [];

  // Función para procesar productos del pedido
  const processOrderProducts = (order: any) => {
    if (!order.lines) return [];

    const products: any[] = [];

    // Función auxiliar para construir URL completa de imagen
    const buildImageUrl = (imageHash: string) => {
      if (!imageHash) return '';
      if (imageHash.startsWith('http')) return imageHash;
      return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
    };

    order.lines.forEach((line: any) => {
      if (line.pack && line.pack.lines) {
        // Es un pack - agregar cada producto del pack
        line.pack.lines.forEach((packLine: any) => {
          products.push({
            id: packLine.product_id?.toString() || 'unknown',
            name: packLine.product?.name || 'Producto',
            code: packLine.product?.code || '',
            image: buildImageUrl(packLine.product?.image_hash || ''),
            quantity: packLine.qty || 1,
            status: 'completed' as const, // Por ahora asumimos completado
          });
        });
      } else if (line.product) {
        // Es un producto individual
        products.push({
          id: line.product_id?.toString() || line.id?.toString(),
          name: line.name,
          code: line.product.code,
          image: buildImageUrl(line.product.image_hash),
          quantity: line.qty,
          status: 'completed' as const, // Por ahora asumimos completado
        });
      }
    });

    return products;
  };

  // Obtener fecha de completado - ahora siempre retorna null ya que no persistimos
  const getCompletedAt = (): string | null => {
    return null;
  };

  // Handler para abrir el modal de detalles del pedido
  const handleOrderClick = (order: any) => {
    // Obtener fechas para el modal
    const lastShipmentUpdate = (order as any)?.shipment?.last_shipment_update || null;
    const apiCompletedDate =
      (order as any).date_tracking_update ||
      (order as any).order_tracking_update ||
      (order as any).tracking_updated_at ||
      null;
    const processedAt = (order as any)?.processed_at || null;

    // Convertir el order de la API al formato esperado por el modal
    const orderDetails: OrderDetails = {
      id: order.id.toString(),
      orderNumber: order.ref,
      customerName: order.customer?.name || '',
      customerLogo: order.shop?.image_hash || '',
      date: order.order_created_at,
      status: order.status,
      statusId: order.status_id, // Agregar status_id
      lastShipmentUpdate, // Agregar fecha de envío
      apiCompletedDate, // Agregar fecha de completado de API
      processedAt, // Agregar fecha de procesado
      totalQuantity: getTotalProducts(order),
      products: processOrderProducts(order),
    };
    setSelectedOrder(orderDetails);
  };

  // Abrir modal cuando selectedOrder cambie
  useEffect(() => {
    if (selectedOrder) {
      orderDetailsModalRef.current?.openModal();
    }
  }, [selectedOrder]);

  // Función para manejar la impresión desde la lista
  const handlePrintFromList = async (order: any) => {
    if (order.id) {
      await printOrder(order.id);
    }
  };

  // Mostrar loading si está cargando
  if (ordersLoading) {
    return (
      <SectionBox>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-complementary-text">{t('orders.loading')}</p>
          </div>
        </div>
      </SectionBox>
    );
  }

  // Mostrar error si hay error
  if (ordersError) {
    return (
      <SectionBox>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <p className="text-error mb-2">{t('orders.error')}</p>
            <p className="text-complementary-text text-xs">{ordersError}</p>
          </div>
        </div>
      </SectionBox>
    );
  }

  return (
    <SectionBox>
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <FontAwesomeIcon icon={faShoppingCart} className="w-4 h-4 text-icons" />
        <h3 className="font-medium text-title">{t('orders.title')}</h3>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-icons"
          />
          <Input
            type="text"
            placeholder={t('orders.search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-8 bg-transparent"
            data-testid="orders-search"
          />
        </div>
      </div>

      {/* Orders List - Lista de pedidos con scroll (altura cuando estado completo) */}
      <div className="space-y-3 max-h-[calc(100vh-215px)] overflow-y-auto">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-complementary-text text-xs">{t('orders.no_orders')}</p>
          </div>
        ) : (
          filteredOrders.map((order: any, index: number) => (
            <div
              key={order.id}
              className={`py-3 ${index < filteredOrders.length - 1 ? 'border-b border-lines' : ''}`}
              data-testid={`order-${order.id}`}
            >
              {/* Order Card - Tarjeta individual de pedido */}
              <div className="space-y-2">
                {/* Order Header - Encabezado del pedido */}
                <div className="flex items-center justify-between">
                  {/* Shop Info - Información de la tienda (clickeable) */}
                  <div
                    className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOrderClick(order);
                    }}
                    data-testid={`shop-info-${order.id}`}
                  >
                    {/* Shop Avatar - Avatar de la tienda */}
                    <Avatar className="w-8 h-8 border border-card-border">
                      <AvatarImage src={order.shop?.image_hash || ''} alt={order.shop?.name || ''} />
                      <AvatarFallback className="bg-card-border text-xs font-medium">
                        {(order.shop?.name || '').slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    {/* Shop Details - Detalles de la tienda */}
                    <div>
                      <div className="text-sm font-medium text-title">{order.ref || ''}</div>
                      <div className="text-xs text-subtext">{order.shop?.name || ''}</div>
                    </div>
                  </div>
                  {/* Order Actions - Acciones del pedido */}
                  <div className="flex items-center gap-3">
                    {/* Quantity Display - Mostrador de cantidad */}
                    <div
                      className="flex items-center gap-2 cursor-pointer hover:opacity-80"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOrderClick(order);
                      }}
                      data-testid={`order-quantity-${order.id}`}
                    >
                      <FontAwesomeIcon icon={faShoppingBag} className="w-3 h-3 text-icons" />
                      <span className="text-xs text-title">{getTotalProducts(order)}</span>
                    </div>
                    {/* Print Button - Botón de imprimir */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="py-2 px-3 hover:bg-hover-press"
                      data-testid={`print-button-${order.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrintFromList(order);
                      }}
                      disabled={isOrderLoading(order.id)}
                    >
                      <FontAwesomeIcon icon={faPrint} className="!w-3 !h-3 text-icons" />
                    </Button>
                  </div>
                </div>
                {/* Order Footer - Pie del pedido */}
                <div className="flex items-center gap-2 justify-between">
                  {/* Status / Fecha Badge */}
                  {(() => {
                    const isPreparationComplete = (data?.preparation?.status || 0) === 3;
                    const isOrderPacked = order.status_id === 6;
                    const lastShipmentUpdate = (order as any)?.shipment?.last_shipment_update || null;
                    const apiCompletedDate =
                      (order as any).date_tracking_update ||
                      (order as any).order_tracking_update ||
                      (order as any).tracking_updated_at ||
                      null;
                    const completedAt = getCompletedAt();

                    // Caso 1: pedido ya está packeado (status_id = 6) → mostrar fecha API
                    if (isOrderPacked) {
                      const dateToShow =
                        (order as any)?.processed_at ||
                        (order as any)?.shipment?.last_shipment_update ||
                        order.order_created_at;
                      return (
                        <Badge
                          variant="outline"
                          className="text-xs bg-card-border light:bg-bg-success text-success border-0 flex items-center gap-2 line-clamp-1"
                        >
                          <FontAwesomeIcon icon={faCalendar} className="w-3 h-3 mr-2" />
                          {dateToShow
                            ? new Date(dateToShow).toLocaleDateString('es-ES', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : t('packing_orders_modal.status_prepared')}
                        </Badge>
                      );
                    }

                    // Caso 2: preparación completa → mostrar fecha API (prioridad) o memoria
                    if (isPreparationComplete) {
                      const dateToShow = lastShipmentUpdate || apiCompletedDate || completedAt;
                      return (
                        <Badge
                          variant="outline"
                          className="text-xs bg-card-border light:bg-bg-success text-success border-0 flex items-center gap-2 line-clamp-1"
                        >
                          <FontAwesomeIcon icon={faCalendar} className="w-3 h-3 mr-2" />
                          {dateToShow
                            ? new Date(dateToShow).toLocaleDateString('es-ES', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : t('packing_orders_modal.status_prepared')}
                        </Badge>
                      );
                    }
                    // Caso 3: en preparación → si la orden se completó en memoria, mostrar su fecha
                    if (completedAt) {
                      return (
                        <Badge
                          variant="outline"
                          className="text-xs bg-card-border light:bg-bg-success text-success border-0 flex items-center gap-2 line-clamp-1"
                        >
                          <FontAwesomeIcon icon={faCalendar} className="w-3 h-3 mr-2" />
                          {new Date(completedAt).toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Badge>
                      );
                    }
                    return (
                      <Badge
                        variant="outline"
                        className="text-xs bg-card-border light:bg-badge-bg-status-3 text-badge-status-3 border-0 flex items-center gap-2"
                      >
                        <FontAwesomeIcon icon={faCalendar} className="w-3 h-3" />
                        {t('orders.status_preparing')}
                      </Badge>
                    );
                  })()}
                  {/* Courier Component - Componente del courier */}
                  <Shop src={order.shipment?.courier?.image_hash || ''} code={order.shipment?.tracking_number || ''} />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de detalles del pedido */}
      <OrderDetailsModal ref={orderDetailsModalRef} order={selectedOrder} />
    </SectionBox>
  );
};
